/**
 * Prop Symbol Styles
 *
 * Factory functions for creating prop symbol layers with shadows.
 */

import type { LayerSpecification } from "maplibre-gl";
import type { PropConfig, ShadowDirection } from "../themes/types";

/**
 * Options for prop symbol layers
 */
export interface PropSymbolOptions {
  /** If true, filter by density_rank progressively at higher zooms */
  isNatureProp?: boolean;
  /** Minimum zoom level to display this prop */
  minZoom?: number;
  /** Global shadow direction (default: SE = {x:1, y:1}) */
  shadowDirection?: ShadowDirection;
  /** Zoom offset for map size presets (default 0). Shifts all zoom stops. */
  zoomOffset?: number;
}

/**
 * Create symbol layers for props (trees, rocks, lamps, etc).
 * Renders point features using icon sprites with optional offset shadow.
 *
 * Returns [shadowLayer, iconLayer] when shadow enabled, or [iconLayer] when disabled.
 * Shadow uses icon-translate (like building depthEffect) for consistent zoom behavior.
 *
 * @param id - Layer ID (shadow gets -shadow suffix)
 * @param sourceId - GeoJSON source ID
 * @param spriteSlug - Sprite name from sprite sheet (e.g., "tree", "bench")
 * @param config - Optional prop config from theme (controls icon, size, opacity, shadow)
 * @param options - Optional rendering options (density filtering, minZoom)
 * @returns Array of layer specs ([shadowLayer, iconLayer] or [iconLayer])
 */
export function createPropSymbolLayer(
  id: string,
  sourceId: string,
  spriteSlug: string,
  config?: PropConfig,
  options?: PropSymbolOptions
): LayerSpecification[] {
  // Use config values or defaults
  const iconSize = config?.iconSize ?? 0.06;
  const iconOpacity = config?.iconOpacity ?? 1;
  const minZoom = options?.minZoom ?? config?.minZoom;
  const iconImage = config?.icon ?? spriteSlug;
  const zo = options?.zoomOffset ?? 0;

  // Build opacity expression: fade in over 0.5 zoom levels above minZoom
  const opacityExpression =
    minZoom !== undefined
      ? [
          "interpolate",
          ["linear"],
          ["zoom"],
          minZoom,
          0,
          minZoom + 0.5,
          iconOpacity,
        ]
      : iconOpacity;

  // Nature props: progressive density filtering by zoom level
  // Calibrated for z16-z20 zoom range, shifted by zoomOffset for larger maps
  // Uses coalesce fallback so existing props without density_rank (=0) are always visible
  const densityFilter = options?.isNatureProp
    ? [
        "step",
        ["zoom"],
        false, // below first stop: hide all nature props
        16 + zo,
        ["<=", ["coalesce", ["get", "density_rank"], 0], 0.1], // 10%
        17 + zo,
        ["<=", ["coalesce", ["get", "density_rank"], 0], 0.3], // 30%
        18 + zo,
        ["<=", ["coalesce", ["get", "density_rank"], 0], 0.6], // 60%
        19 + zo,
        true, // show all (100%)
      ]
    : undefined;

  // Shared icon-size expression (used by both shadow and main icon)
  // Base 2 scaling matches physical world physics (area doubles each zoom)
  const iconSizeExpr: unknown = [
    "interpolate",
    ["exponential", 2],
    ["zoom"],
    15 + zo, iconSize * 0.125,  // 1/2^3
    16 + zo, iconSize * 0.25,   // 1/2^2
    17 + zo, iconSize * 0.5,    // 1/2
    18 + zo, iconSize,          // reference
    19 + zo, iconSize * 2,      // 2x
    20 + zo, iconSize * 4,      // 2^2
    21 + zo, iconSize * 8,      // 2^3
  ];

  const layers: LayerSpecification[] = [];

  // Shadow layer (optional, controlled by config.shadow)
  // Uses circle layer for blur effect, sized to match icon footprint
  const shadow = config?.shadow;
  if (shadow?.enabled) {
    const shadowOpacity = shadow.opacity ?? 0.3;
    const shadowBlur = shadow.blur ?? 0.5;
    const shadowScale = shadow.scale ?? 0.8;
    const dirX = options?.shadowDirection?.x ?? 1;
    const dirY = options?.shadowDirection?.y ?? 1;

    // Calculate shadow radius to match icon footprint
    // Sprites are 64px after normalization, radius = (iconSize * 64 / 2) * scale
    const baseRadius = iconSize * 32 * shadowScale;

    // Build translate expression based on zoom-dependent or fixed offset
    let translateExpr: unknown;
    if (shadow.offsetZoom) {
      // Zoom-dependent offset interpolation, shifted by zoomOffset
      const { farOffset, closeOffset } = shadow.offsetZoom;
      translateExpr = [
        "interpolate",
        ["linear"],
        ["zoom"],
        16 + zo, ["literal", [farOffset * dirX, farOffset * dirY]],
        20 + zo, ["literal", [closeOffset * dirX, closeOffset * dirY]],
      ];
    } else {
      // Fixed offset with damped exponential scaling (√2 base ≈ 0.5 damping)
      const shadowOffset = shadow.offset ?? 2;
      const s = shadowOffset;
      translateExpr = [
        "interpolate",
        ["exponential", 1.41],
        ["zoom"],
        15 + zo, ["literal", [s * 0.354 * dirX, s * 0.354 * dirY]],
        16 + zo, ["literal", [s * 0.5 * dirX, s * 0.5 * dirY]],
        17 + zo, ["literal", [s * 0.707 * dirX, s * 0.707 * dirY]],
        18 + zo, ["literal", [s * dirX, s * dirY]],
        19 + zo, ["literal", [s * 1.414 * dirX, s * 1.414 * dirY]],
        20 + zo, ["literal", [s * 2 * dirX, s * 2 * dirY]],
        21 + zo, ["literal", [s * 2.828 * dirX, s * 2.828 * dirY]],
      ];
    }

    // Zoom-gated opacity: fade out below z15 (shifted by zoomOffset)
    const fadeMinZoom = 14 + zo;
    const fadeMaxZoom = 15 + zo;
    const gatedShadowOpacityExpr = minZoom !== undefined
      ? [
          "interpolate", ["linear"], ["zoom"],
          Math.min(minZoom, fadeMinZoom), 0,
          Math.max(minZoom + 0.5, fadeMaxZoom), shadowOpacity,
        ]
      : [
          "interpolate", ["linear"], ["zoom"],
          fadeMinZoom, 0,
          fadeMaxZoom, shadowOpacity,
        ];

    layers.push({
      id: `${id}-shadow`,
      type: "circle",
      source: sourceId,
      ...(densityFilter && { filter: densityFilter }),
      paint: {
        "circle-color": "#000000",
        "circle-opacity": gatedShadowOpacityExpr,
        "circle-blur": shadowBlur,
        // Base 2 scaling to match icon size changes
        "circle-radius": [
          "interpolate",
          ["exponential", 2],
          ["zoom"],
          15 + zo, baseRadius * 0.125,
          16 + zo, baseRadius * 0.25,
          17 + zo, baseRadius * 0.5,
          18 + zo, baseRadius,
          19 + zo, baseRadius * 2,
          20 + zo, baseRadius * 4,
          21 + zo, baseRadius * 8,
        ],
        "circle-translate": translateExpr,
        "circle-translate-anchor": "map",
        "circle-pitch-alignment": "map",
      },
    } as unknown as LayerSpecification);
  }

  // Main icon layer - on top
  layers.push({
    id,
    type: "symbol",
    source: sourceId,
    ...(densityFilter && { filter: densityFilter }),
    layout: {
      "icon-image": iconImage,
      "icon-size": iconSizeExpr,
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      "icon-padding": 0,
      "icon-rotation-alignment": "map",
      "icon-pitch-alignment": "map",
    },
    paint: {
      "icon-opacity": opacityExpression,
    },
  } as unknown as LayerSpecification);

  return layers;
}
